import React, { useState } from 'react';
import { Card, Avatar, Tag, Typography, Space, Rate, Progress, Tooltip, Checkbox } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  CalendarOutlined, 
  UserOutlined,
  CheckCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { 
  calculateDuration, 
  calculateSubtaskProgress, 
  getDescriptionPreview, 
  shouldShowReadMore 
} from './utils';
import { PRIORITY_CONFIG, DIFFICULTY_CONFIG } from './constants';

const { Text, Paragraph } = Typography;

const TaskCardRefactored = ({ task, onView, onToggleDone, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ 
    id: task.id,
    transition: {
      duration: 200,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    }
  });

  const [showFullDescription, setShowFullDescription] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.7 : 1,
  };

  const duration = calculateDuration(task.startDate, task.endDate);
  const subtaskProgress = calculateSubtaskProgress(task.subtasks);
  const descriptionPreview = getDescriptionPreview(task.description);
  const hasLongDescription = shouldShowReadMore(task.description);

  const getCardStyle = () => ({
    marginBottom: 0,
    cursor: isSortableDragging ? 'grabbing' : 'grab',
    border: `1px solid ${isDragging ? '#1890ff' : '#d9d9d9'}`,
    borderRadius: '8px',
    boxShadow: isDragging 
      ? '0 12px 32px rgba(24, 144, 255, 0.35)'
      : '0 6px 20px rgba(0, 0, 0, 0.15)',
    transform: isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    background: task.column === 'done' ? '#f6ffed' : '#ffffff',
    overflow: 'hidden',
    position: 'relative',
  });

  const handleDescriptionClick = (e) => {
    e.stopPropagation();
    setShowFullDescription(!showFullDescription);
  };

  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.Medium;
  const difficulty = DIFFICULTY_CONFIG[task.difficulty] || DIFFICULTY_CONFIG[2];

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes}
      {...listeners}
    >
      <Card 
        size="small" 
        style={getCardStyle()}
        hoverable={!isSortableDragging}
        bodyStyle={{ 
          padding: '12px',
          position: 'relative'
        }}
        onClick={(e) => {
          if (!isSortableDragging) {
            onView(task);
          }
        }}
      >
        {/* Priority Indicator Bar */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '3px',
            background: `linear-gradient(to bottom, ${priority.color}, ${priority.color}80)`,
          }}
        />

        {/* Header Row - Title, Priority and Done checkbox */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '8px',
          marginLeft: '4px'
        }}>
          <Text 
            strong 
            style={{ 
              fontSize: '13px',
              lineHeight: '1.4',
              flex: 1,
              marginRight: '8px',
              color: '#262626',
              textDecoration: task.column === 'done' ? 'line-through' : 'none'
            }}
            ellipsis={{ tooltip: task.content }}
          >
            {task.content}
          </Text>
          
          <Space size={4}>
            <Tooltip title={task.column === 'done' ? 'Mark as not done' : 'Mark as done'}>
              <Checkbox
                checked={task.column === 'done'}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleDone?.(task.id)}
              />
            </Tooltip>
            <div 
              style={{
                padding: '2px 6px',
                background: priority.bgColor,
                border: `1px solid ${priority.color}30`,
                borderRadius: '4px',
                fontSize: '10px',
                color: priority.color,
                fontWeight: '600',
                minWidth: '20px',
                textAlign: 'center'
              }}
            >
              {priority.icon}
            </div>
          </Space>
        </div>

        {/* Enhanced Description Preview */}
        {task.description && (
          <div style={{ 
            marginBottom: '8px', 
            marginLeft: '4px',
            position: 'relative'
          }}>
            <Tooltip 
              title={showFullDescription ? "Click to collapse" : "Click to expand"} 
              placement="top"
            >
              <div
                onClick={handleDescriptionClick}
                style={{ 
                  cursor: hasLongDescription ? 'pointer' : 'default',
                  padding: '4px 0',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (hasLongDescription) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasLongDescription) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Paragraph
                  style={{ 
                    fontSize: '11px',
                    lineHeight: '1.4',
                    color: '#666',
                    margin: 0,
                    display: '-webkit-box',
                    WebkitLineClamp: showFullDescription ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word'
                  }}
                >
                  {showFullDescription ? task.description : descriptionPreview}
                </Paragraph>
                
                {/* Read More/Less Indicator */}
                {hasLongDescription && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px',
                    marginTop: '4px'
                  }}>
                    <Text 
                      style={{ 
                        fontSize: '9px', 
                        color: '#1890ff',
                        fontWeight: '500'
                      }}
                    >
                      {showFullDescription ? 'Show less' : 'Read more'}
                    </Text>
                    <EyeOutlined style={{ fontSize: '10px', color: '#1890ff' }} />
                  </div>
                )}
              </div>
            </Tooltip>

            {/* Description Stats Badge */}
            {task.description.length > 200 && (
              <div style={{ 
                position: 'absolute',
                top: '2px',
                right: '2px',
                background: 'rgba(24, 144, 255, 0.1)',
                borderRadius: '8px',
                padding: '1px 6px'
              }}>
                <Text style={{ fontSize: '8px', color: '#1890ff', fontWeight: '600' }}>
                  {Math.ceil(task.description.length / 1000)}k
                </Text>
              </div>
            )}
          </div>
        )}

        {/* Subtasks Progress Bar */}
        {task.subtasks && task.subtasks.length > 0 && (
          <div style={{ marginBottom: '8px', marginLeft: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <Text style={{ fontSize: '10px', color: '#8c8c8c' }}>
                <CheckCircleOutlined style={{ marginRight: '4px', fontSize: '9px' }} />
                Progress
              </Text>
              <Text strong style={{ fontSize: '10px', color: '#1890ff' }}>
                {subtaskProgress}%
              </Text>
            </div>
            <Progress 
              percent={subtaskProgress} 
              size="small"
              showInfo={false}
              strokeColor={{
                '0%': '#1890ff',
                '100%': '#722ed1',
              }}
              style={{ margin: 0 }}
            />
            <Text type="secondary" style={{ fontSize: '9px', marginTop: '2px' }}>
              {task.subtasks.filter(st => st.completed).length}/{task.subtasks.length} tasks
            </Text>
          </div>
        )}

        {/* Metadata Row - Assignee and Dates */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '8px',
          marginLeft: '4px'
        }}>
          <Space size={6}>
            <Avatar 
              size={20}
              style={{ 
                backgroundColor: '#1890ff',
                fontSize: '10px'
              }}
              icon={<UserOutlined />}
            />
            <Text style={{ fontSize: '11px', color: '#595959' }}>
              {task.assignee.split(' ')[0]}
            </Text>
          </Space>

          <Space size={6}>
            <CalendarOutlined style={{ fontSize: '10px', color: '#8c8c8c' }} />
            <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
              {duration}d
            </Text>
          </Space>
        </div>

        {/* Footer Row - Labels and Difficulty */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginLeft: '4px'
        }}>
          {/* Labels */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {task.labels && task.labels.length > 0 && (
              <Space size={4} wrap>
                {task.labels.slice(0, 2).map((label, index) => (
                  <Tag
                    key={label}
                    style={{
                      background: '#f0f0f0',
                      color: '#666',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '9px',
                      padding: '0 6px',
                      height: '18px',
                      lineHeight: '18px',
                      margin: 0,
                      maxWidth: '60px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {label}
                  </Tag>
                ))}
                {task.labels.length > 2 && (
                  <Tag
                    style={{
                      background: '#e6f7ff',
                      color: '#1890ff',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '9px',
                      padding: '0 6px',
                      height: '18px',
                      lineHeight: '18px',
                      margin: 0
                    }}
                  >
                    +{task.labels.length - 2}
                  </Tag>
                )}
              </Space>
            )}
          </div>

          {/* Difficulty */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            padding: '2px 6px',
            background: '#fafafa',
            borderRadius: '10px',
            border: '1px solid #f0f0f0'
          }}>
            <Rate 
              disabled 
              value={task.difficulty} 
              style={{ fontSize: '10px' }} 
              character="★"
            />
            <Text style={{ 
              fontSize: '9px', 
              color: difficulty.color,
              fontWeight: '600',
              minWidth: '12px'
            }}>
              {task.difficulty}
            </Text>
          </div>
        </div>

        {/* Hover Effect Indicator */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.03) 0%, transparent 50%)',
            opacity: isSortableDragging ? 0 : 0.5,
            transition: 'opacity 0.2s ease',
            borderRadius: '7px',
          }}
        />

        {/* Enhanced Drag Handle Area */}
        <div
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '16px',
            height: '16px',
            background: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '3px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            cursor: 'grab',
          }}
          className="drag-handle"
        >
          <div style={{ 
            width: '100%', 
            height: '2px', 
            background: '#bfbfbf',
            borderRadius: '1px',
            position: 'relative',
          }}>
            <div style={{ 
              position: 'absolute',
              top: '4px',
              left: 0,
              right: 0,
              height: '2px', 
              background: '#bfbfbf',
              borderRadius: '1px',
            }}/>
            <div style={{ 
              position: 'absolute',
              top: '8px',
              left: 0,
              right: 0,
              height: '2px', 
              background: '#bfbfbf',
              borderRadius: '1px',
            }}/>
          </div>
        </div>
      </Card>

      <style jsx>{`
        .drag-handle {
          opacity: 0;
        }
        div:hover .drag-handle {
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default TaskCardRefactored;
